/** Marketing i18n fragment — intgGitlab (integrations/gitlab). */

export const intgGitlabEn = {
  meta: {
    title: "GitLab integration — merge requests proposed by AI, reviewed by your team",
    description:
      "Connect GitLab and FixControl delivers proposed changes as merge requests: a diff, the evidence behind it, and an approval recorded before anything is written. Your team reviews and merges. Health is tracked per capability and writes go through an audited outbox.",
  },
  hero: {
    eyebrow: "GitLab integration",
    title: "The output is always",
    highlight: "a merge request",
    subtitle:
      "FixControl uses your GitLab connection to read repository and branch context and to open merge requests carrying a proposed patch. Review and merge happen in GitLab, by your team.",
    primaryCta: "Book a demo",
    secondaryCta: "See remediation",
    bullet1: "MR delivery only",
    bullet2: "Merge stays with you",
    bullet3: "Capability health",
  },
  what: {
    eyebrow: "What it does",
    title: "What the GitLab connection is used for",
  },
  features: {
    eyebrow: "In detail",
    title: "How a proposed MR comes together",
    repoContext: {
      title: "Repository & branch context",
      body: "Repository and branch context comes from your GitLab connection, read inside the bound project and tenant. That context is what a proposed patch is built against.",
    },
    proposedMr: {
      title: "Proposed merge requests",
      body: "With a connection that permits it, FixControl opens a merge request carrying the patch. When someone opens the merge request from the patch, they first see its title, description and commit message exactly as GitLab will receive them, and can edit them. Merging it is not something FixControl does.",
    },
    diff: {
      title: "Diff & evidence",
      body: "Reviewers get a standard MR: the diff, plus the evidence that motivated it. Nothing about the change is hidden in a side channel.",
    },
    humanMerge: {
      title: "Human merge & deploy",
      body: "Merge and deploy decisions belong to your team, in GitLab and in your pipeline. FixControl opens the merge request and from then on only follows it: it picks up that the MR was merged or closed and records that on the work behind it, so the trail ends on the same fact your team acted on. It never merges anything itself.",
    },
    scoped: {
      title: "Scoped permissions",
      body: "Everything runs under the permissions of the GitLab connection you create. Grant less, and FixControl can do less — that is the intended shape.",
    },
    audit: {
      title: "Evidence & audit",
      body: "Each opened MR has a matching entry in the decision ledger, recorded when the change was approved for delivery.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "One connection, several separately probed capabilities",
    description:
      "Reading a project and opening a merge request are different permissions, and they can be in different states. FixControl checks and displays them independently, so you know before delivery whether the MR path works.",
    readContext: {
      name: "Repository & branch read",
      body: "Repository and branch context is read through the connection, inside the bound project and tenant.",
    },
    openMr: {
      name: "Open MR with proposed patch",
      body: "Needs the connection to hold MR-creation permission on the target project. Dispatched through the integration outbox.",
    },
    diffEvidence: {
      name: "Diff & evidence on the MR",
      body: "The proposed change is reviewable as a standard merge request diff, with evidence attached.",
    },
    mergeAuto: {
      name: "Unattended merge or deploy",
      body: "Absent by design: FixControl opens MRs and does not merge or deploy. Those remain your team’s calls.",
    },
    singleGreen: {
      name: "One green status for everything",
      body: "Replaced by per-capability status. A connection that reads fine but can’t open MRs shows exactly that.",
    },
  },
  governed: {
    eyebrow: "Failure handling",
    title: "An MR that didn’t open is a visible failure",
    noteTitle: "Retries, dead-letters, ledger entries",
    noteBody:
      "MR creation goes through the integration outbox — retried when transient, dead-lettered when not, and always visible on the operational timeline. The approval behind the change sits in the decision ledger with actor, role, channel, evidence, and consequence; the merge itself is done by someone on your team in GitLab.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "About the GitLab integration",
    q1: {
      q: "Does FixControl merge merge requests or deploy on its own?",
      a: "It doesn’t. FixControl’s output is an open merge request; your team reviews it in GitLab and decides whether it merges and ships.",
    },
    q2: {
      q: "What does FixControl need to open an MR?",
      a: "A GitLab connection with permission to open merge requests on the target project — the permissions you grant when you create the connection. Without them, reads can keep working while MR-open reports unavailable.",
    },
    q3: {
      q: "Where do engineers review a proposed change?",
      a: "In GitLab, as a standard merge request. The diff and the supporting evidence are on the MR, and the timeline in FixControl shows the same change.",
    },
    q4: {
      q: "What happens if opening an MR fails?",
      a: "The write waits in the outbox and retries. If GitLab keeps rejecting it, the job dead-letters and the failure is flagged on the timeline instead of the MR quietly not existing.",
    },
    q5: {
      q: "Are GitLab actions audited?",
      a: "Yes. The approval that released the change is in the decision ledger, and the MR records the delivery. Auditors can walk from one to the other.",
    },
    q6: {
      q: "Can FixControl govern our GitLab CI deployments too?",
      a: "Where configured during a pilot, yes. A deployment your pipeline is already holding — a deployment approval or a manual job — opens a FixControl approval, and the verdict is carried out through GitLab's own primitive. A private GitLab connects through the FC Agent, outbound only. FixControl never starts a pipeline or a deployment.",
    },
  },
  related: {
    supportEng: {
      label: "Support → engineering",
      description: "A support ticket becomes a reviewable MR, gate by gate.",
    },
    github: {
      label: "GitHub",
      description: "The same delivery model on GitHub, as pull requests.",
    },
    deployGov: {
      label: "Deployment governance",
      description: "Merge first, then a human-approved deployment gate.",
    },
  },
  cta: {
    title: "Review an AI-proposed merge request",
    description:
      "Book a demo and follow one change from ticket to open MR, with every approval on record.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgGitlabNl = {
  meta: {
    title: "GitLab-integratie — merge requests voorgesteld door AI, gereviewd door je team",
    description:
      "Koppel GitLab en FixControl levert voorgestelde wijzigingen als merge requests: een diff, het bewijs erachter en een goedkeuring die is vastgelegd voordat er iets wordt geschreven. Je team reviewt en merget. Health wordt per capability bijgehouden en schrijfacties lopen via een geauditeerde outbox.",
  },
  hero: {
    eyebrow: "GitLab-integratie",
    title: "De uitkomst is altijd",
    highlight: "een merge request",
    subtitle:
      "FixControl gebruikt je GitLab-koppeling om repository- en branch-context te lezen en om merge requests met een voorgestelde patch te openen. Reviewen en mergen gebeuren in GitLab, door je team.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk remediatie",
    bullet1: "Alleen MR-levering",
    bullet2: "Mergen blijft bij jou",
    bullet3: "Capability-health",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Waarvoor de GitLab-koppeling wordt gebruikt",
  },
  features: {
    eyebrow: "In detail",
    title: "Hoe een voorgestelde MR tot stand komt",
    repoContext: {
      title: "Repository- & branch-context",
      body: "Repository- en branch-context komt uit je GitLab-koppeling, gelezen binnen het gekoppelde project en de tenant. Tegen die context wordt een voorgestelde patch gebouwd.",
    },
    proposedMr: {
      title: "Voorgestelde merge requests",
      body: "Met een koppeling die het toestaat, opent FixControl een merge request met de patch. Wie de merge request vanuit de patch opent, ziet eerst de titel, beschrijving en commitmelding precies zoals GitLab ze ontvangt, en kan die nog aanpassen. Het mergen ervan doet FixControl niet.",
    },
    diff: {
      title: "Diff & bewijs",
      body: "Reviewers krijgen een standaard-MR: de diff, plus het bewijs dat hem motiveerde. Niets aan de wijziging zit verstopt in een zijkanaal.",
    },
    humanMerge: {
      title: "Menselijke merge & deploy",
      body: "Merge- en deploybeslissingen horen bij je team, in GitLab en in je pipeline. FixControl opent de merge request en volgt hem daarna alleen nog: het pikt op dat de MR gemerged of gesloten is en legt dat vast bij het werk erachter, zodat het spoor eindigt op hetzelfde feit waarop je team handelde. Zelf mergen doet het nooit.",
    },
    scoped: {
      title: "Gescopete permissies",
      body: "Alles draait onder de permissies van de GitLab-koppeling die je aanmaakt. Geef minder, en FixControl kan minder — zo is het bedoeld.",
    },
    audit: {
      title: "Bewijs & audit",
      body: "Elke geopende MR heeft een bijpassende regel in het beslis-grootboek, vastgelegd toen de wijziging voor levering werd goedgekeurd.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Eén koppeling, meerdere apart geteste capabilities",
    description:
      "Een project lezen en een merge request openen zijn verschillende permissies, en ze kunnen in verschillende staten verkeren. FixControl controleert en toont ze onafhankelijk, zodat je vóór levering weet of het MR-pad werkt.",
    readContext: {
      name: "Repository- & branch-lezen",
      body: "Repository- en branch-context wordt via de koppeling gelezen, binnen het gekoppelde project en de tenant.",
    },
    openMr: {
      name: "MR openen met voorgestelde patch",
      body: "Vereist dat de koppeling MR-aanmaakpermissie heeft op het doelproject. Verstuurd via de integratie-outbox.",
    },
    diffEvidence: {
      name: "Diff & bewijs op de MR",
      body: "De voorgestelde wijziging is te reviewen als een standaard merge request-diff, met bewijs erbij.",
    },
    mergeAuto: {
      name: "Onbeheerd mergen of deployen",
      body: "Bewust afwezig: FixControl opent MR’s en merget of deployt niet. Dat blijven de keuzes van je team.",
    },
    singleGreen: {
      name: "Eén groene status voor alles",
      body: "Vervangen door status per capability. Een koppeling die prima leest maar geen MR’s kan openen, toont precies dat.",
    },
  },
  governed: {
    eyebrow: "Foutafhandeling",
    title: "Een MR die niet openging, is een zichtbare fout",
    noteTitle: "Retries, dead-letters, grootboekregels",
    noteBody:
      "Een MR aanmaken loopt via de integratie-outbox — opnieuw geprobeerd bij tijdelijke fouten, naar de dead-letter bij hardnekkige, en altijd zichtbaar op de operationele tijdlijn. De goedkeuring achter de wijziging staat in het beslis-grootboek met actor, rol, kanaal, bewijs en consequentie; de merge zelf doet iemand van je eigen team in GitLab.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Over de GitLab-integratie",
    q1: {
      q: "Merget FixControl merge requests of deployt het op eigen houtje?",
      a: "Nee. De uitkomst van FixControl is een geopende merge request; je team reviewt hem in GitLab en beslist of hij merget en uitgaat.",
    },
    q2: {
      q: "Wat heeft FixControl nodig om een MR te openen?",
      a: "Een GitLab-koppeling met permissie om merge requests te openen op het doelproject — de permissies die je geeft bij het aanmaken van de koppeling. Zonder die kan lezen blijven werken terwijl MR-openen onbeschikbaar rapporteert.",
    },
    q3: {
      q: "Waar reviewen engineers een voorgestelde wijziging?",
      a: "In GitLab, als een standaard merge request. De diff en het onderbouwende bewijs staan op de MR, en de tijdlijn in FixControl toont dezelfde wijziging.",
    },
    q4: {
      q: "Wat gebeurt er als het openen van een MR mislukt?",
      a: "De schrijfactie wacht in de outbox en wordt opnieuw geprobeerd. Blijft GitLab hem weigeren, dan gaat de job naar de dead-letter en wordt de fout op de tijdlijn gemarkeerd — geen MR die stil ontbreekt.",
    },
    q5: {
      q: "Worden GitLab-acties geaudit?",
      a: "Ja. De goedkeuring die de wijziging vrijgaf staat in het beslis-grootboek, en de MR legt de levering vast. Auditors kunnen van de een naar de ander lopen.",
    },
    q6: {
      q: "Kan FixControl ook onze GitLab CI-deployments governen?",
      a: "Waar geconfigureerd tijdens een pilot, ja. Een deployment die je pipeline al vasthoudt — een deployment-approval of een handmatige job — opent een FixControl-goedkeuring, en het verdict wordt uitgevoerd via GitLabs eigen primitief. Een private GitLab koppelt via de FC Agent, alleen uitgaand. FixControl start nooit een pipeline of deployment.",
    },
  },
  related: {
    supportEng: {
      label: "Support → engineering",
      description: "Een supportticket wordt een reviewbare MR, poort voor poort.",
    },
    github: {
      label: "GitHub",
      description: "Hetzelfde leveringsmodel op GitHub, als pull requests.",
    },
    deployGov: {
      label: "Deployment-governance",
      description: "Eerst mergen, dan een door een mens goedgekeurde deployment-poort.",
    },
  },
  cta: {
    title: "Review een door AI voorgestelde merge request",
    description:
      "Boek een demo en volg één wijziging van ticket tot geopende MR, met elke goedkeuring vastgelegd.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
